import {
  useEffect,
  useMemo,
  useState,
  type ChangeEvent,
  type Dispatch,
  type FormEvent,
  type SetStateAction,
} from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  Activity,
  CalendarDays,
  Download,
  Gauge,
  Plus,
  Save,
  Trash2,
  TrendingUp,
  Wallet,
  X,
} from "lucide-react";
import Cleave from "cleave.js/react";
import ReactQuill from "react-quill";
import Select, { type StylesConfig } from "react-select";
import { toast } from "react-toastify";
import {
  MesaMidiaBasePayload,
  MesaMidiaData,
  MesaMidiaDiarioPayload,
  MesaMidiaGraficoGastoItem,
  MesaMidiaHistoricoItem,
  MesaMidiaPacingStatus,
  MesaMidiaPropostaItem,
  ServiceMesaMidiaAcompanhamento,
} from "@/services/serviceMesaMidiaAcompanhamento";
import { ModalBase } from "@/components/ModalBase/ModalBase";

type MesaMidiaAcompanhamentoProps = {
  desk: {
    id: number;
  };
  onRefreshTimeline?: () => void;
};

type BaseFormState = {
  orcamento_total: string;
  data_inicial: string;
  data_final: string;
  impressoes_contratadas: string;
  meta_diaria_investimento: string;
};

type DiarioFormState = {
  data: string;
  gasto_dia: string;
  impressoes_dia: string;
  cliques: string;
  conversoes: string;
  meta_diaria: string;
  observacoes: string;
};

type RangeOption = {
  value: string;
  label: string;
};

type ProposalItemOption = {
  value: string;
  label: string;
};

const rangeOptions: RangeOption[] = [
  { value: "7", label: "Últimos 7 dias" },
  { value: "15", label: "Últimos 15 dias" },
  { value: "30", label: "Últimos 30 dias" },
  { value: "all", label: "Todo histórico" },
];

const selectStyles: StylesConfig<RangeOption, false> = {
  control: (base, state) => ({
    ...base,
    minHeight: 34,
    borderColor: state.isFocused ? "#153DCE" : "#E5E7EB",
    borderRadius: 999,
    boxShadow: "none",
    fontSize: 12,
    "&:hover": { borderColor: "#153DCE" },
  }),
  menu: (base) => ({ ...base, zIndex: 9999, fontSize: 12 }),
  singleValue: (base) => ({ ...base, color: "#1F1F1F" }),
  indicatorSeparator: () => ({ display: "none" }),
};

const proposalItemSelectStyles: StylesConfig<ProposalItemOption, false> = {
  control: (base, state) => ({
    ...base,
    minHeight: 42,
    borderColor: state.isFocused ? "#153DCE" : "#E5E7EB",
    borderRadius: 12,
    boxShadow: "none",
    fontSize: 13,
    "&:hover": { borderColor: "#153DCE" },
  }),
  menu: (base) => ({ ...base, zIndex: 9999, fontSize: 13 }),
  singleValue: (base) => ({ ...base, color: "#1F1F1F" }),
  indicatorSeparator: () => ({ display: "none" }),
};

const emptyBaseForm: BaseFormState = {
  orcamento_total: "",
  data_inicial: "",
  data_final: "",
  impressoes_contratadas: "",
  meta_diaria_investimento: "",
};

const emptyDiarioForm: DiarioFormState = {
  data: new Date().toISOString().slice(0, 10),
  gasto_dia: "",
  impressoes_dia: "",
  cliques: "",
  conversoes: "",
  meta_diaria: "",
  observacoes: "",
};

const statusClass: Record<MesaMidiaPacingStatus, string> = {
  dentro_do_previsto: "bg-verde-300/15 text-verde-300 border-verde-300/30",
  atencao: "bg-goon-amareloClaro/70 text-cinza-800 border-amber-300",
  critico: "bg-red-100 text-red-600 border-red-200",
};

function formatMoney(value?: number | string | null) {
  return Number(value || 0).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
}

function formatNumber(value?: number | string | null) {
  return Number(value || 0).toLocaleString("pt-BR");
}

function formatPercent(value?: number | null) {
  return `${Number(value || 0).toLocaleString("pt-BR", {
    maximumFractionDigits: 1,
  })}%`;
}

function dateInput(value?: string | null) {
  return value ? String(value).slice(0, 10) : "";
}

function dateLabel(value?: string | null) {
  if (!value) return "-";
  return new Date(`${String(value).slice(0, 10)}T00:00:00`).toLocaleDateString(
    "pt-BR",
  );
}

function inclusiveDaysBetween(start?: string | null, end?: string | null) {
  if (!start || !end) return 0;
  const startTime = Date.parse(`${String(start).slice(0, 10)}T00:00:00Z`);
  const endTime = Date.parse(`${String(end).slice(0, 10)}T00:00:00Z`);
  if (!Number.isFinite(startTime) || !Number.isFinite(endTime)) return 0;
  if (endTime < startTime) return 0;
  return Math.floor((endTime - startTime) / 86_400_000) + 1;
}

function stripHtml(value?: string | null) {
  return String(value || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function currencyFormValue(value?: number | string | null) {
  const numberValue = Number(value || 0);
  return numberValue > 0
    ? numberValue.toLocaleString("pt-BR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    : "";
}

function numberFormValue(value?: number | string | null) {
  const numberValue = Number(value || 0);
  return numberValue > 0 ? String(Math.round(numberValue)) : "";
}

function getResponseMessage(response: unknown, fallback: string) {
  const maybeResponse = response as { data?: { msg?: string } } | undefined;
  return maybeResponse?.data?.msg || fallback;
}

export function MesaMidiaAcompanhamento({
  desk,
  onRefreshTimeline,
}: MesaMidiaAcompanhamentoProps) {
  const queryClient = useQueryClient();
  const [baseForm, setBaseForm] = useState<BaseFormState>(emptyBaseForm);
  const [diarioForm, setDiarioForm] =
    useState<DiarioFormState>(emptyDiarioForm);
  const [range, setRange] = useState<RangeOption>(rangeOptions[0]);
  const [dailyModalOpen, setDailyModalOpen] = useState(false);
  const [selectedItemKey, setSelectedItemKey] = useState("");

  const queryKey = [
    "mesa-midia-acompanhamento",
    desk.id,
    selectedItemKey || "default",
  ];
  const acompanhamentoQuery = useQuery({
    queryKey,
    queryFn: async () => {
      const response = await ServiceMesaMidiaAcompanhamento.getResumo(
        desk.id,
        selectedItemKey || undefined,
      );
      if (!response || response.status !== 200) {
        throw new Error(getResponseMessage(response, "Erro ao buscar dados"));
      }
      return response.data.data;
    },
    enabled: Boolean(desk?.id),
  });

  const acompanhamento = acompanhamentoQuery.data;

  useEffect(() => {
    if (!acompanhamento?.proposta_item_key) return;
    if (selectedItemKey !== acompanhamento.proposta_item_key) {
      setSelectedItemKey(acompanhamento.proposta_item_key);
    }
  }, [acompanhamento?.proposta_item_key, selectedItemKey]);

  useEffect(() => {
    if (!acompanhamento?.base) return;
    setBaseForm({
      orcamento_total: currencyFormValue(acompanhamento.base.orcamento_total),
      data_inicial: dateInput(acompanhamento.base.data_inicial),
      data_final: dateInput(acompanhamento.base.data_final),
      impressoes_contratadas: numberFormValue(
        acompanhamento.base.impressoes_contratadas,
      ),
      meta_diaria_investimento: currencyFormValue(
        acompanhamento.base.meta_diaria_investimento,
      ),
    });
    setDiarioForm((current) => ({
      ...current,
      meta_diaria:
        current.meta_diaria ||
        currencyFormValue(acompanhamento.base.meta_diaria_investimento),
    }));
  }, [acompanhamento?.base, acompanhamento?.proposta_item_key]);

  const propostaItemOptions = useMemo<ProposalItemOption[]>(() => {
    return (acompanhamento?.itens || []).map((item) => ({
      value: item.key,
      label: item.label,
    }));
  }, [acompanhamento?.itens]);

  const selectedPropostaItem =
    acompanhamento?.itens.find((item) => item.key === selectedItemKey) ||
    acompanhamento?.item;
  const selectedPropostaItemOption =
    propostaItemOptions.find((item) => item.value === selectedItemKey) ||
    propostaItemOptions[0] ||
    null;
  const currentItemKey =
    selectedItemKey || acompanhamento?.proposta_item_key || "desk";
  const entregaPluralLabel =
    acompanhamento?.base.entrega_plural_label ||
    selectedPropostaItem?.entrega_plural_label ||
    "Entregas";
  const entregaSingularLabel =
    acompanhamento?.base.entrega_label ||
    selectedPropostaItem?.entrega_label ||
    "Entrega";
  const deliveryCostLabel = String(
    acompanhamento?.base.unidade_compra || selectedPropostaItem?.unidade_compra || "",
  )
    .toUpperCase()
    .includes("CPM")
    ? "CPM"
    : "Custo médio";

  const updateBaseMutation = useMutation({
    mutationFn: async (payload: MesaMidiaBasePayload) => {
      const response = await ServiceMesaMidiaAcompanhamento.updateBase(
        desk.id,
        { ...payload, proposta_item_key: currentItemKey },
      );
      if (!response || response.status !== 200) {
        throw new Error(
          getResponseMessage(response, "Erro ao salvar dados-base"),
        );
      }
      return response.data;
    },
    onSuccess: () => {
      toast.success("Dados-base atualizados");
      queryClient.invalidateQueries({ queryKey });
      onRefreshTimeline?.();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const saveDiarioMutation = useMutation({
    mutationFn: async (payload: MesaMidiaDiarioPayload) => {
      const response = await ServiceMesaMidiaAcompanhamento.saveDiario(
        desk.id,
        { ...payload, proposta_item_key: currentItemKey },
      );
      if (!response || response.status !== 200) {
        throw new Error(
          getResponseMessage(response, "Erro ao salvar atualização diária"),
        );
      }
      return response.data;
    },
    onSuccess: () => {
      toast.success("Atualização diária salva");
      setDailyModalOpen(false);
      setDiarioForm((current) => ({
        ...emptyDiarioForm,
        data: current.data,
        meta_diaria: current.meta_diaria,
      }));
      queryClient.invalidateQueries({ queryKey });
      onRefreshTimeline?.();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const deleteMutation = useMutation({
    mutationFn: async (registroId: number) => {
      const response = await ServiceMesaMidiaAcompanhamento.deleteDiario(
        desk.id,
        registroId,
      );
      if (!response || response.status !== 200) {
        throw new Error(getResponseMessage(response, "Erro ao remover registro"));
      }
      return response.data;
    },
    onSuccess: () => {
      toast.success("Registro removido");
      queryClient.invalidateQueries({ queryKey });
      onRefreshTimeline?.();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const historico = useMemo(() => {
    const items = [...(acompanhamento?.dashboard.historico || [])].reverse();
    if (range.value === "all") return items;
    return items.slice(0, Number(range.value));
  }, [acompanhamento?.dashboard.historico, range.value]);

  const chartData = useMemo(() => {
    const limit = range.value === "all" ? undefined : Number(range.value);
    const items: MesaMidiaGraficoGastoItem[] =
      acompanhamento?.dashboard.grafico_gasto_diario || [];
    return (limit ? items.slice(-limit) : items).map((item) => ({
      ...item,
      label: dateLabel(item.data).slice(0, 5),
    }));
  }, [acompanhamento?.dashboard.grafico_gasto_diario, range.value]);

  const deliveryChartData = useMemo(() => {
    const limit = range.value === "all" ? undefined : Number(range.value);
    const items = acompanhamento?.dashboard.historico || [];
    const totalDays = inclusiveDaysBetween(
      acompanhamento?.base.data_inicial,
      acompanhamento?.base.data_final,
    );
    const dailyTarget =
      totalDays > 0
        ? Number(acompanhamento?.base.impressoes_contratadas || 0) / totalDays
        : 0;

    return (limit ? items.slice(-limit) : items).map((item) => ({
      label: dateLabel(item.data).slice(0, 5),
      entrega_dia: Number(item.impressoes || 0),
      meta_diaria_entrega: dailyTarget,
    }));
  }, [
    acompanhamento?.base.data_final,
    acompanhamento?.base.data_inicial,
    acompanhamento?.base.impressoes_contratadas,
    acompanhamento?.dashboard.historico,
    range.value,
  ]);

  function handleBaseSubmit(event: FormEvent) {
    event.preventDefault();
    updateBaseMutation.mutate(baseForm);
  }

  function handleDiarioSubmit(event: FormEvent) {
    event.preventDefault();
    if (
      !diarioForm.data ||
      !diarioForm.gasto_dia ||
      !diarioForm.impressoes_dia
    ) {
      toast.error(
        `Preencha data, gasto e ${entregaSingularLabel.toLowerCase()} do dia`,
      );
      return;
    }
    saveDiarioMutation.mutate(diarioForm);
  }

  function exportHistory() {
    const rows = [
      [
        "Data",
        "Gasto diário",
        entregaPluralLabel,
        deliveryCostLabel,
        "% entregue",
        "% investimento consumido",
        "Status",
        "Observações",
      ],
      ...historico.map((item) => [
        dateLabel(item.data),
        String(item.gasto_dia).replace(".", ","),
        String(item.impressoes),
        String(item.cpm.toFixed(2)).replace(".", ","),
        String(item.percentual_entregue.toFixed(2)).replace(".", ","),
        String(item.percentual_investimento_consumido.toFixed(2)).replace(
          ".",
          ",",
        ),
        item.status_label,
        stripHtml(item.observacoes),
      ]),
    ];
    const csv = rows
      .map((row) =>
        row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(";"),
      )
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `acompanhamento-mesa-midia-desk-${desk.id}-${currentItemKey}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }

  if (acompanhamentoQuery.isLoading) {
    return (
      <section className="rounded-2xl border border-cinza-200 bg-white p-5">
        <div className="h-5 w-56 animate-pulse rounded bg-cinza-100" />
        <div className="mt-4 grid gap-3 md:grid-cols-4">
          {[0, 1, 2, 3].map((item) => (
            <div
              key={item}
              className="h-24 animate-pulse rounded-xl bg-cinza-100"
            />
          ))}
        </div>
      </section>
    );
  }

  if (acompanhamentoQuery.isError || !acompanhamento) {
    return (
      <section className="rounded-2xl border border-red-100 bg-red-50 p-5 text-sm text-red-600">
        Não foi possível carregar o acompanhamento da Mesa de Mídia.
      </section>
    );
  }

  return (
    <section className="space-y-4 rounded-2xl border border-cinza-200 bg-white p-3 sm:p-5">
      <Header
        acompanhamento={acompanhamento}
        onNewDaily={() => setDailyModalOpen(true)}
      />

      <ProposalItemSelector
        itens={acompanhamento.itens}
        selectedItem={selectedPropostaItem}
        selectedOption={selectedPropostaItemOption}
        options={propostaItemOptions}
        onChange={(option) => {
          if (!option || option.value === currentItemKey) return;
          setSelectedItemKey(option.value);
          setDailyModalOpen(false);
        }}
      />

      <form
        onSubmit={handleBaseSubmit}
        className="grid gap-3 rounded-2xl border border-cinza-200 bg-cinza-50 p-3 md:grid-cols-5"
      >
        <CurrencyField
          label="Orçamento total"
          required
          value={baseForm.orcamento_total}
          onChange={(value) =>
            setBaseForm((current) => ({ ...current, orcamento_total: value }))
          }
        />
        <TextField
          label="Data inicial"
          required
          type="date"
          value={baseForm.data_inicial}
          onChange={(value) =>
            setBaseForm((current) => ({ ...current, data_inicial: value }))
          }
        />
        <TextField
          label="Data final"
          required
          type="date"
          value={baseForm.data_final}
          onChange={(value) =>
            setBaseForm((current) => ({ ...current, data_final: value }))
          }
        />
        <IntegerField
          label={`Volume contratado (${entregaPluralLabel})`}
          required
          value={baseForm.impressoes_contratadas}
          onChange={(value) =>
            setBaseForm((current) => ({
              ...current,
              impressoes_contratadas: value,
            }))
          }
        />
        <div className="grid gap-2 sm:grid-cols-[1fr_auto] md:col-span-5">
          <CurrencyField
            label="Meta diária"
            value={baseForm.meta_diaria_investimento}
            onChange={(value) =>
              setBaseForm((current) => ({
                ...current,
                meta_diaria_investimento: value,
              }))
            }
          />
          <button
            type="submit"
            disabled={updateBaseMutation.isPending}
            className="mt-auto inline-flex min-h-[42px] items-center justify-center gap-2 rounded-lg bg-azul-400 px-4 text-sm font-semibold text-white transition hover:bg-azul-400/90 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <Save size={16} />
            Salvar base
          </button>
        </div>
      </form>

      <KpiGrid
        acompanhamento={acompanhamento}
        entregaPluralLabel={entregaPluralLabel}
      />

      <div className="grid gap-4 xl:grid-cols-[1fr_360px]">
        <div className="grid min-w-0 gap-4 2xl:grid-cols-2">
          <SpendChart chartData={chartData} />
          <DeliveryChart
            chartData={deliveryChartData}
            entregaPluralLabel={entregaPluralLabel}
          />
        </div>
        <SideIndicators
          acompanhamento={acompanhamento}
          entregaPluralLabel={entregaPluralLabel}
        />
      </div>

      <div className="rounded-2xl border border-cinza-200 bg-white p-3 sm:p-4">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="text-base font-semibold text-cinza-800">
              Acompanhamento diário
            </h3>
            <p className="text-xs text-cinza-400">
              Histórico de gasto, entrega e pacing por data.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-40">
              <Select
                value={range}
                onChange={(option) => option && setRange(option)}
                options={rangeOptions}
                styles={selectStyles}
                isSearchable={false}
              />
            </div>
            <button
              type="button"
              onClick={exportHistory}
              disabled={historico.length === 0}
              className="inline-flex h-9 items-center gap-2 rounded-full border border-cinza-200 px-3 text-xs font-semibold text-azul-400 transition hover:border-azul-400 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Download size={14} />
              Exportar
            </button>
          </div>
        </div>
        <HistoryTable
          historico={historico}
          entregaPluralLabel={entregaPluralLabel}
          deliveryCostLabel={deliveryCostLabel}
          onDelete={(id) => deleteMutation.mutate(id)}
          deletingId={
            typeof deleteMutation.variables === "number"
              ? deleteMutation.variables
              : undefined
          }
          isDeleting={deleteMutation.isPending}
        />
      </div>

      <DailyUpdateModal
        open={dailyModalOpen}
        form={diarioForm}
        setForm={setDiarioForm}
        onClose={() => setDailyModalOpen(false)}
        onSubmit={handleDiarioSubmit}
        isSaving={saveDiarioMutation.isPending}
        entregaPluralLabel={entregaPluralLabel}
      />
    </section>
  );
}

function ProposalItemSelector({
  itens,
  selectedItem,
  selectedOption,
  options,
  onChange,
}: {
  itens: MesaMidiaPropostaItem[];
  selectedItem?: MesaMidiaPropostaItem;
  selectedOption: ProposalItemOption | null;
  options: ProposalItemOption[];
  onChange: (option: ProposalItemOption | null) => void;
}) {
  const hasMultipleItems = itens.length > 1;

  return (
    <div className="rounded-2xl border border-azul-400/15 bg-azul-400/5 p-3">
      <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_320px] lg:items-end">
        <div className="min-w-0">
          <p className="text-xs font-semibold uppercase tracking-wide text-azul-400">
            Item em análise
          </p>
          <h3 className="mt-1 break-words text-base font-semibold text-cinza-800">
            {selectedItem?.label || "Configuração geral da Desk"}
          </h3>
          <p className="mt-1 text-xs leading-relaxed text-cinza-500">
            {selectedItem?.resumo ||
              "Sem itens de proposta cadastrados para esta Desk."}
          </p>
        </div>
        {hasMultipleItems && (
          <label className="block text-xs font-medium text-cinza-500">
            Selecionar linha da proposta
            <Select
              value={selectedOption}
              onChange={(option) => onChange(option)}
              options={options}
              styles={proposalItemSelectStyles}
              isSearchable={false}
              className="mt-1"
            />
          </label>
        )}
      </div>
    </div>
  );
}

function Header({
  acompanhamento,
  onNewDaily,
}: {
  acompanhamento: MesaMidiaData;
  onNewDaily: () => void;
}) {
  const dashboard = acompanhamento.dashboard;
  return (
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div>
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-azul-400">
          <Gauge size={16} />
          Trading Desk
        </div>
        <h2 className="mt-1 text-xl font-semibold text-cinza-800">
          Acompanhamento da campanha
        </h2>
        <p className="text-sm text-cinza-400">
          Investimento, entrega e ritmo diário da Mesa de Mídia.
        </p>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <span
          className={`rounded-full border px-3 py-1 text-xs font-semibold ${
            statusClass[dashboard.pacing_status]
          }`}
        >
          {dashboard.pacing_label}
        </span>
        <button
          type="button"
          onClick={onNewDaily}
          className="inline-flex h-9 items-center justify-center gap-2 rounded-full bg-azul-400 px-4 text-xs font-semibold text-white transition hover:bg-azul-400/90"
        >
          <Plus size={14} />
          Atualizar dia
        </button>
      </div>
    </div>
  );
}

function KpiGrid({
  acompanhamento,
  entregaPluralLabel,
}: {
  acompanhamento: MesaMidiaData;
  entregaPluralLabel: string;
}) {
  const dashboard = acompanhamento.dashboard;
  const cards = [
    {
      label: "Orçamento total",
      value: formatMoney(dashboard.orcamento_total),
      detail: "Base da campanha",
      icon: Wallet,
      className: "bg-[#F7F8FF] border-azul-400/20",
    },
    {
      label: "Gasto acumulado",
      value: formatMoney(dashboard.gasto_acumulado),
      detail: `${formatPercent(dashboard.percentual_orcamento_consumido)} consumido`,
      icon: TrendingUp,
      className: "bg-[#F5FFF0] border-verde-300/30",
    },
    {
      label: "Custo médio diário",
      value: formatMoney(dashboard.custo_medio_diario),
      detail: "Média dos dias lançados",
      icon: Activity,
      className: "bg-white border-cinza-200",
    },
    {
      label: `${entregaPluralLabel} contratadas`,
      value: formatNumber(dashboard.impressoes_contratadas),
      detail: `${formatPercent(dashboard.percentual_entrega)} entregue`,
      icon: Gauge,
      className: "bg-white border-cinza-200",
    },
    {
      label: `${entregaPluralLabel} entregues`,
      value: formatNumber(dashboard.impressoes_entregues),
      detail: `${formatNumber(dashboard.impressoes_restantes)} restantes`,
      icon: Gauge,
      className: "bg-white border-cinza-200",
    },
    {
      label: "Residual investimento",
      value: formatMoney(dashboard.residual_investimento),
      detail: `${formatPercent(100 - dashboard.percentual_orcamento_consumido)} disponível`,
      icon: Wallet,
      className: "bg-[#FFF9DA] border-goon-amareloClaro",
    },
    {
      label: "Residual de dias",
      value: `${formatNumber(dashboard.dias_restantes)} dias`,
      detail: `${formatPercent(dashboard.percentual_periodo_restante)} restante`,
      icon: CalendarDays,
      className: "bg-white border-cinza-200",
    },
    {
      label: "Pacing",
      value: dashboard.pacing_label,
      detail: "Status da campanha",
      icon: Gauge,
      className: "bg-[#EEF6FF] border-azul-400/20",
    },
  ];

  return (
    <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
      {cards.map((card) => {
        const Icon = card.icon;
        return (
          <article
            key={card.label}
            className={`rounded-2xl border p-4 ${card.className}`}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-xs font-semibold text-cinza-400">
                  {card.label}
                </p>
                <strong className="mt-1 block break-words text-xl font-semibold text-cinza-800">
                  {card.value}
                </strong>
                <span className="text-xs text-cinza-400">{card.detail}</span>
              </div>
              <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-white text-azul-400 shadow-sm">
                <Icon size={18} />
              </span>
            </div>
          </article>
        );
      })}
    </div>
  );
}

function DailyUpdateModal({
  open,
  form,
  setForm,
  onClose,
  onSubmit,
  isSaving,
  entregaPluralLabel,
}: {
  open: boolean;
  form: DiarioFormState;
  setForm: Dispatch<SetStateAction<DiarioFormState>>;
  onClose: () => void;
  onSubmit: (event: FormEvent) => void;
  isSaving: boolean;
  entregaPluralLabel: string;
}) {
  const update = (key: keyof DiarioFormState, value: string) => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  return (
    <ModalBase
      open={open}
      onOpenChange={(nextOpen) => !nextOpen && onClose()}
      className="w-[min(94vw,820px)] rounded-lg bg-white p-0"
      title="Abastecimento diário"
    >
      <form onSubmit={onSubmit} className="max-h-[84vh] overflow-y-auto">
        <div className="sticky top-0 z-10 flex items-start justify-between gap-3 border-b border-cinza-200 bg-white px-5 py-4">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="text-lg font-semibold text-cinza-800">
                Abastecimento diário
              </h3>
              <span className="rounded-full bg-azul-400/10 px-2 py-1 text-[10px] font-semibold text-azul-400">
                Entrada manual
              </span>
            </div>
            <p className="mt-1 text-xs leading-relaxed text-cinza-500">
              Informe os dados do dia para atualizar KPIs, histórico e pacing da
              campanha.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="grid h-9 w-9 shrink-0 place-items-center rounded-lg border border-cinza-200 text-cinza-500 transition hover:border-azul-400 hover:text-azul-400"
            title="Fechar"
          >
            <X size={16} />
          </button>
        </div>

        <div className="space-y-4 px-5 py-4">
          <section className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <TextField
              label="Data"
              required
              type="date"
              value={form.data}
              onChange={(value) => update("data", value)}
            />
            <CurrencyField
              label="Gasto do dia"
              required
              value={form.gasto_dia}
              onChange={(value) => update("gasto_dia", value)}
            />
            <CurrencyField
              label="Meta diária"
              value={form.meta_diaria}
              onChange={(value) => update("meta_diaria", value)}
            />
            <IntegerField
              label={`Volume entregue no dia (${entregaPluralLabel})`}
              required
              value={form.impressoes_dia}
              onChange={(value) => update("impressoes_dia", value)}
            />
            <IntegerField
              label="Cliques"
              value={form.cliques}
              onChange={(value) => update("cliques", value)}
            />
            <IntegerField
              label="Conversões"
              value={form.conversoes}
              onChange={(value) => update("conversoes", value)}
            />
          </section>

          <RichTextField
            label="Observações"
            value={form.observacoes}
            onChange={(value) => update("observacoes", value)}
          />
        </div>

        <div className="sticky bottom-0 flex flex-wrap justify-end gap-2 border-t border-cinza-200 bg-white px-5 py-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-cinza-200 px-4 py-2 text-sm text-cinza-700 transition hover:border-cinza-300"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={isSaving}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-azul-400 px-4 py-2 text-sm font-semibold text-white transition hover:bg-azul-400/90 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <Save size={16} />
            {isSaving ? "Salvando..." : "Salvar atualização"}
          </button>
        </div>
      </form>
    </ModalBase>
  );
}

function SpendChart({
  chartData,
}: {
  chartData: Array<{ label: string; gasto_dia: number; meta_diaria: number }>;
}) {
  return (
    <div className="rounded-2xl border border-cinza-200 bg-white p-4">
      <h3 className="text-base font-semibold text-cinza-800">
        Gasto diário x meta diária
      </h3>
      <div className="mt-4 h-72">
        {chartData.length > 0 ? (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip
                formatter={(value: number | string, name: string) => [
                  formatMoney(Number(value)),
                  name === "gasto_dia" ? "Gasto diário" : "Meta diária",
                ]}
              />
              <Bar dataKey="gasto_dia" fill="#153DCE" radius={[8, 8, 0, 0]} />
              <Bar dataKey="meta_diaria" fill="#BFCBFF" radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <EmptyState text="Nenhuma atualização diária registrada." />
        )}
      </div>
    </div>
  );
}

function DeliveryChart({
  chartData,
  entregaPluralLabel,
}: {
  chartData: Array<{
    label: string;
    entrega_dia: number;
    meta_diaria_entrega: number;
  }>;
  entregaPluralLabel: string;
}) {
  return (
    <div className="min-w-0 rounded-2xl border border-cinza-200 bg-white p-4">
      <h3 className="text-base font-semibold text-cinza-800">
        {entregaPluralLabel}: entrega diária x meta diária
      </h3>
      <div className="mt-4 h-72">
        {chartData.length > 0 ? (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} />
              <YAxis
                tick={{ fontSize: 11 }}
                tickFormatter={(value: number) => formatNumber(value)}
                width={64}
              />
              <Tooltip
                formatter={(value: number | string, name: string) => [
                  formatNumber(Number(value)),
                  name === "entrega_dia"
                    ? `${entregaPluralLabel} no dia`
                    : `Meta diária de ${entregaPluralLabel.toLowerCase()}`,
                ]}
              />
              <Bar dataKey="entrega_dia" fill="#153DCE" radius={[8, 8, 0, 0]} />
              <Bar
                dataKey="meta_diaria_entrega"
                fill="#BFCBFF"
                radius={[8, 8, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <EmptyState text="Nenhuma atualização diária registrada." />
        )}
      </div>
    </div>
  );
}

function SideIndicators({
  acompanhamento,
  entregaPluralLabel,
}: {
  acompanhamento: MesaMidiaData;
  entregaPluralLabel: string;
}) {
  const dashboard = acompanhamento.dashboard;
  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-cinza-200 bg-white p-4">
        <h3 className="text-base font-semibold text-cinza-800">
          Contratado x entregue
        </h3>
        <div className="mt-4 h-4 overflow-hidden rounded-full bg-cinza-100">
          <div
            className="h-full rounded-full bg-azul-400 transition-all"
            style={{
              width: `${Math.min(dashboard.percentual_entrega, 100)}%`,
            }}
          />
        </div>
        <div className="mt-3 flex justify-between gap-3 text-xs text-cinza-500">
          <span>
            <strong className="block text-sm text-cinza-800">
              {formatNumber(dashboard.impressoes_entregues)}
            </strong>
            {entregaPluralLabel} entregues
          </span>
          <span className="text-right">
            <strong className="block text-sm text-cinza-800">
              {formatNumber(dashboard.impressoes_contratadas)}
            </strong>
            {entregaPluralLabel} contratadas
          </span>
        </div>
      </div>
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-1 2xl:grid-cols-2">
        <ProgressCircle
          label="Residual de investimento"
          value={100 - dashboard.percentual_orcamento_consumido}
          detail={`${formatMoney(dashboard.residual_investimento)} disponível`}
          color="#FFB020"
        />
        <ProgressCircle
          label="Residual de dias"
          value={dashboard.percentual_periodo_restante}
          detail={`${formatNumber(dashboard.dias_restantes)} dias restantes`}
          color="#7B3FF2"
        />
      </div>
      <div className="rounded-2xl border border-azul-400/10 bg-[#EEF6FF] p-4">
        <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-azul-400">
          <Activity size={16} />
          Insight rápido
        </div>
        <p className="text-sm leading-relaxed text-cinza-700">
          {dashboard.insight}
        </p>
        <p className="mt-2 text-xs text-cinza-500">
          Período decorrido: {formatPercent(dashboard.percentual_periodo_decorrido)}.
        </p>
      </div>
    </div>
  );
}

function ProgressCircle({
  label,
  value,
  detail,
  color,
}: {
  label: string;
  value: number;
  detail: string;
  color: string;
}) {
  const percentValue = Math.max(0, Math.min(value, 100));
  return (
    <article className="rounded-2xl border border-cinza-200 bg-white p-4">
      <div className="flex items-center gap-4">
        <div
          className="grid h-20 w-20 shrink-0 place-items-center rounded-full"
          style={{
            background: `conic-gradient(${color} ${percentValue}%, #EFEFEF ${percentValue}% 100%)`,
          }}
        >
          <div className="grid h-14 w-14 place-items-center rounded-full bg-white text-sm font-semibold text-cinza-800">
            {formatPercent(percentValue)}
          </div>
        </div>
        <div>
          <h4 className="text-sm font-semibold text-cinza-800">{label}</h4>
          <p className="mt-1 text-xs leading-relaxed text-cinza-500">{detail}</p>
        </div>
      </div>
    </article>
  );
}

function HistoryTable({
  historico,
  entregaPluralLabel,
  deliveryCostLabel,
  onDelete,
  deletingId,
  isDeleting,
}: {
  historico: MesaMidiaHistoricoItem[];
  entregaPluralLabel: string;
  deliveryCostLabel: string;
  onDelete: (id: number) => void;
  deletingId?: number;
  isDeleting: boolean;
}) {
  if (historico.length === 0) {
    return <EmptyState text="Nenhum registro diário encontrado." />;
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-[900px] w-full text-left text-xs">
        <thead className="text-[11px] uppercase text-cinza-400">
          <tr className="border-b border-cinza-100">
            <th className="px-2 py-3">Data</th>
            <th className="px-2 py-3">Gasto diário</th>
            <th className="px-2 py-3">{entregaPluralLabel}</th>
            <th className="px-2 py-3">{deliveryCostLabel}</th>
            <th className="px-2 py-3">% entregue</th>
            <th className="px-2 py-3">% investimento</th>
            <th className="px-2 py-3">Status</th>
            <th className="px-2 py-3 text-right">Ações</th>
          </tr>
        </thead>
        <tbody>
          {historico.map((item) => (
            <tr
              key={item.id}
              className="border-b border-cinza-100 last:border-b-0"
            >
              <td className="px-2 py-3 text-cinza-700">{dateLabel(item.data)}</td>
              <td className="px-2 py-3 font-medium text-cinza-800">
                {formatMoney(item.gasto_dia)}
              </td>
              <td className="px-2 py-3 text-cinza-700">
                {formatNumber(item.impressoes)}
              </td>
              <td className="px-2 py-3 text-cinza-700">
                {formatMoney(item.cpm)}
              </td>
              <td className="px-2 py-3 text-cinza-700">
                {formatPercent(item.percentual_entregue)}
              </td>
              <td className="px-2 py-3 text-cinza-700">
                {formatPercent(item.percentual_investimento_consumido)}
              </td>
              <td className="px-2 py-3">
                <span
                  className={`rounded-full border px-2 py-1 text-[11px] font-semibold ${
                    statusClass[item.status]
                  }`}
                >
                  {item.status_label}
                </span>
              </td>
              <td className="px-2 py-3 text-right">
                <button
                  type="button"
                  onClick={() => onDelete(item.id)}
                  disabled={isDeleting && deletingId === item.id}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-full text-cinza-400 transition hover:bg-red-50 hover:text-red-600 disabled:cursor-not-allowed disabled:opacity-50"
                  title="Remover registro"
                >
                  <Trash2 size={15} />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TextField({
  label,
  value,
  onChange,
  type = "text",
  required,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
  required?: boolean;
}) {
  return (
    <label className="block text-xs font-medium text-cinza-500">
      {label} {required && <span className="text-red-500">*</span>}
      <input
        type={type}
        required={required}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="mt-1 h-[42px] w-full rounded-lg border border-cinza-200 bg-white px-3 text-sm text-cinza-800 outline-none transition focus:border-azul-400"
      />
    </label>
  );
}

function CurrencyField({
  label,
  value,
  onChange,
  required,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
}) {
  return (
    <label className="block text-xs font-medium text-cinza-500">
      {label} {required && <span className="text-red-500">*</span>}
      <Cleave
        value={value}
        onChange={(event: ChangeEvent<HTMLInputElement>) => onChange(event.target.value)}
        options={{
          numeral: true,
          numeralDecimalMark: ",",
          delimiter: ".",
          numeralDecimalScale: 2,
          prefix: "R$ ",
          rawValueTrimPrefix: true,
        }}
        className="mt-1 h-[42px] w-full rounded-lg border border-cinza-200 bg-white px-3 text-sm text-cinza-800 outline-none transition focus:border-azul-400"
        placeholder="R$ 0,00"
        required={required}
      />
    </label>
  );
}

function IntegerField({
  label,
  value,
  onChange,
  required,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
}) {
  return (
    <label className="block text-xs font-medium text-cinza-500">
      {label} {required && <span className="text-red-500">*</span>}
      <input
        type="text"
        inputMode="numeric"
        value={value}
        onChange={(event) => onChange(event.target.value.replace(/\D/g, ""))}
        className="mt-1 h-[42px] w-full rounded-lg border border-cinza-200 bg-white px-3 text-sm text-cinza-800 outline-none transition focus:border-azul-400"
        required={required}
      />
    </label>
  );
}

function RichTextField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <label className="block text-xs font-medium text-cinza-500">
      {label}
      <div className="mt-1 overflow-hidden rounded-lg border border-cinza-200 bg-white">
        <ReactQuill
          theme="snow"
          value={value}
          onChange={onChange}
          modules={{
            toolbar: [["bold", "italic", "underline"], [{ list: "bullet" }]],
          }}
        />
      </div>
    </label>
  );
}

function EmptyState({ text }: { text: string }) {
  return (
    <div className="grid min-h-[120px] place-items-center rounded-xl border border-dashed border-cinza-200 bg-white px-4 py-8 text-center text-sm text-cinza-400">
      {text}
    </div>
  );
}
